import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/cn';
import { HoverCard } from '../../components/ui/HoverCard';
import { AUTHOR, authorLinks } from '../../constants/author';

// Stopka z informacją o autorze. Imię i nazwisko otwiera kartę z kontaktami
// po najechaniu myszą, dotknięciu lub przejściu tabulatorem.

function CopyableRow({ link }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(link.copyValue);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Schowek bywa niedostępny (brak HTTPS, odmowa uprawnień) —
      // wartość i tak jest widoczna na karcie, więc da się ją przepisać.
    }
  }, [link.copyValue]);

  const Icon = link.icon;

  return (
    <button
      type="button"
      onClick={copy}
      className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-elevated"
    >
      <Icon size={14} className="shrink-0 text-muted" />
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-content">{link.label}</span>
        <span className="block truncate text-2xs text-subtle">
          {link.handle || link.copyValue}
        </span>
      </span>
      {copied ? (
        <Check size={13} className="shrink-0 text-success" />
      ) : (
        <Copy size={13} className="shrink-0 text-subtle" />
      )}
    </button>
  );
}

function LinkRow({ link }) {
  const Icon = link.icon;

  return (
    <a
      href={link.href}
      target="_blank"
      rel="noreferrer noopener"
      className="flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-elevated"
    >
      <Icon size={14} className="shrink-0 text-muted" />
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-content">{link.label}</span>
        {link.handle && (
          <span className="block truncate text-2xs text-subtle">{link.handle}</span>
        )}
      </span>
      <ExternalLink size={12} className="shrink-0 text-subtle" />
    </a>
  );
}

function AuthorCardContent() {
  const links = authorLinks();

  return (
    <div>
      <div className="mb-2 border-b border-line pb-2">
        <p className="text-sm font-semibold text-content">{AUTHOR.name}</p>
        <p className="mt-0.5 text-2xs text-muted">{AUTHOR.role}</p>
      </div>

      {links.length === 0 ? (
        <p className="px-2 py-1.5 text-2xs text-subtle">
          Dane kontaktowe nie zostały jeszcze uzupełnione.
        </p>
      ) : (
        <div className="space-y-0.5">
          {links.map((link) =>
            link.copyValue ? (
              <CopyableRow key={link.id} link={link} />
            ) : (
              <LinkRow key={link.id} link={link} />
            )
          )}
        </div>
      )}
    </div>
  );
}

export function AuthorFooter({ className }) {
  return (
    <p className={cn('text-2xs text-subtle', className)}>
      {/* Symbol © — znak praw autorskich. Zapis ® oznacza zastrzeżony znak
          towarowy i w linii copyright byłby nie na miejscu. */}
      Copyright © {AUTHOR.copyrightFrom}–{AUTHOR.copyrightTo}
      <span className="mx-1.5 text-line-strong">|</span>
      {AUTHOR.tagline} by{' '}
      <HoverCard content={<AuthorCardContent />}>
        <button
          type="button"
          className="rounded font-medium text-muted underline decoration-line-strong decoration-dotted underline-offset-2 transition-colors hover:text-accent hover:decoration-accent"
        >
          {AUTHOR.name}
        </button>
      </HoverCard>
    </p>
  );
}

export default AuthorFooter;
